<template>
  <form role="form">
    <template v-if="restrictedTransfer">
      <payout-hint-panel-form-group
        :warning="true"
        :heading="$t('warning_heading')">
        {{ $t('restriction_text_1') }}<br/>
        {{ $t('restriction_text_2') }}
        <template v-slot:label-inner>&nbsp;</template>
      </payout-hint-panel-form-group>
      <br/>
    </template>

    <payout-form-group :label="$t('Номер кошелька')"
                       class="form-group__label_align_start"
                       :required="model.isRequiredAttribute('destinationAccount')"
                       :has-errors="model.errors.has('destinationAccount')">
      <filtered-input-wrapper :disabled="disabledForm"
                              v-model="model.values.destinationAccount"></filtered-input-wrapper>
      <payout-input-hint-block v-slot:hints :hint="$t('account_hint')"></payout-input-hint-block>
      <error-message-block v-slot:errors attribute="destinationAccount|*"
                           :errors="model.errors"></error-message-block>
    </payout-form-group>

    <payout-textfield-input-group :model='model' attribute='description' :disabled="disabledForm"
                                  :label="$t('Назначение платежа')"></payout-textfield-input-group>

    <br/>

    <payout-form-group-section-checkbox
      :label="$t('Перевод с протекцией')"
      v-model="model.values.protection"
      :disabled="disabledForm || !isProtectionAvailable"></payout-form-group-section-checkbox>
    <br/>

    <template v-if="isProtectionAvailable">
      <transition name="fade">
        <div v-show="model.values.protection">
          <payout-textfield-input-group :model="model" attribute="protectionCode"
                                        :required="true"
                                        :label="$t('Код протекции')">
                                        :disabled="disabledForm"
          ></payout-textfield-input-group>

          <payout-form-group :label="$t('Период протекции')" :required="true">
            <div class="input-group">
              <input type="text" class="form-control" v-model="model.values.protectionPeriod" />
              <div class="input-addon">
                {{ $tc('days_plur', normalizedPeriod) }}
              </div>
            </div>
            <payout-input-hint-block v-slot="hints" :hint="$t('protection_period_hint')"></payout-input-hint-block>
            <error-message-block v-slot:errors attribute="protectionPeriod|*"
                                 :errors="model.errors"></error-message-block>
          </payout-form-group>
          <br/>
        </div>
      </transition>
    </template>

    <payout-input-amount-form-group :disabled="disabledForm"
                                    :model="model"></payout-input-amount-form-group>

    <payout-estimated-destination-amount-form-group
      :model="model"></payout-estimated-destination-amount-form-group>

    <payout-destination-currency-conversion-notice-form-group style="margin-top: 1em;"
                                              :model="model"></payout-destination-currency-conversion-notice-form-group>

    <template v-if="!restrictedTransfer">
    <payout-evaluate-fee-form-group :model="model"></payout-evaluate-fee-form-group>
    </template>
    <template v-if="!disabledForm">
      <payout-form-notification-block :model="model"></payout-form-notification-block>
    </template>
    <template v-if="!restrictedTransfer">
    <hr/>
    <payout-group-verification-type :model="model"></payout-group-verification-type>
    </template>
  </form>
</template>

<script lang="ts">
import AbstractPayoutForm from "@/components/forms/payout/abstract/AbstractPayoutForm.vue";
import {Component, Watch} from "vue-property-decorator";
import PayoutFormGroup from "@/components/forms/payout/PayoutFormGroup.vue";
import PayoutInputHintBlock from "@/components/forms/payout/PayoutInputHintBlock.vue";
import ErrorMessageBlock from "@/components/forms/payout/ErrorMessageBlock.vue";
import PayoutTextfieldInputGroup from "@/components/forms/payout/PayoutTextfieldInputGroup.vue";
import PayoutInputAmountFormGroup from "@/components/forms/payout/PayoutInputAmountFormGroup.vue";
import PayoutEstimatedDestinationAmountFormGroup
  from "@/components/forms/payout/forms/PayoutEstimatedDestinationAmountFormGroup.vue";
import PayoutEvaluateFeeFormGroup from "@/components/forms/payout/PayoutEvaluateFeeFormGroup.vue";
import PayoutToggableHintPanelFormGroup
  from "@/components/forms/payout/hint/PayoutToggableHintPanelFormGroup.vue";
import PayoutFormNotificationBlock
  from "@/components/forms/payout/forms/PayoutFormNotificationBlock.vue";
import PayoutGroupVerificationType
  from "@/components/payout/verification/PayoutGroupVerificationType.vue";
import FilteredInputWrapper from "@/components/forms/payout/forms/inputs/FilteredInputWrapper.vue";
import PayoutHintPanel from "@/components/forms/payout/PayoutHintPanel.vue";
import {WebmoneyPayoutModel} from "@/components/forms/payout/forms/emoney/emoney-payments";
import PayoutHintPanelFormGroup from "@/components/forms/payout/hint/PayoutHintPanelFormGroup.vue";
import PayoutFormCheckbox from "@/components/forms/basic/PayoutFormCheckbox.vue";
import PayoutFormGroupSectionCheckbox
  from "@/components/forms/payout/formgroup/PayoutFormGroupSectionCheckbox.vue";
import PayoutDestinationCurrencyConversionNoticeFormGroup
  from "@/components/forms/payout/crypto/PayoutDestinationCurrencyConversionNoticeFormGroup.vue";


@Component({
  name: "WebmoneyPaymentForm",
  components: {
    PayoutDestinationCurrencyConversionNoticeFormGroup,
    PayoutFormGroupSectionCheckbox,
    PayoutFormCheckbox,
    PayoutHintPanelFormGroup,
    PayoutHintPanel,
    FilteredInputWrapper,
    PayoutGroupVerificationType,
    PayoutFormNotificationBlock,
    PayoutToggableHintPanelFormGroup,
    PayoutEvaluateFeeFormGroup,
    PayoutEstimatedDestinationAmountFormGroup,
    PayoutInputAmountFormGroup,
    PayoutTextfieldInputGroup, ErrorMessageBlock, PayoutInputHintBlock, PayoutFormGroup
  }
})
export default class WebmoneyPaymentForm extends AbstractPayoutForm {
  model = new WebmoneyPayoutModel();

  @Watch('submitable')
  onSubmitableUpdate(newVal) {
    this.$emit('model-submitable', newVal);
  }

  get submitable() {
    return (this.model.loaded && !this.model.options.restrictedTransfer);
  };

  get normalizedPeriod() {
    return Number(this.model.values.protectionPeriod);
  }

  get disabledForm(): boolean {
    return this.restrictedTransfer;
  }

  get restrictedTransfer(): boolean {
    return this.model.options.restrictedTransfer;
  }

  get isProtectionAvailable(): boolean {
    return this.model.options.protectionAvailable;
  }
}
</script>

<style scoped>

</style>

<i18n>
{
  "en": {
    "wallets_requirement": "Transfers should only be made to verified wallets.",
    "Номер кошелька": "Wallet number",
    "Назначение платежа": "Description",
    "Лимиты": "Limits",
    "Код протекции": "Protection code",
    "Перевод с протекцией": "Transfer with protection",
    "Период протекции": "Protection period",
    "Информация о лимитах": "Limits",
    "account_hint": "Webmoney wallet. Example: Z123456789012",
    "protection_period_hint": "The period of protection in days. The maximum period is 30 days.",
    "limits": {
      "line1": "Minimum payment: {minamount}, maximum payment: {maxamount} or equivalent in selected currency.",
      "line2": "Month limit per 1 wallet: {maxamount} or equivalent in selected currency."
    },
    "restriction_text_1": "Due to the requirements for compliance with WEBMONEY TRANSFER policies, from 03/27/2020, individuals are limited to withdrawing funds to WebMoney wallets.",
    "restriction_text_2": "If you are a legal entity, you can withdraw funds to WebMoney wallets.",
    "warning_heading": "Webmoney transfers restriction",
    "days_plur": "days|day|days"
  },
  "ru": {
    "protection_period_hint": "Период протекции в днях. Максимальный период 30 дней.",
    "days_plur": "дней|день|дня|дней",
    "warning_heading": "Ограничение переводов Webmoney",
    "account_hint": "Номер кошелька Webmoney. Пример: Z123456789012",
    "wallets_requirement": "Переводы должны осуществляться только на верифицированные кошельки.",
    "limits": {
      "line1": "Минимальный разовый платеж: {minamount}, максимальный: {maxamount} или эквивалент в выбранной валюте.",
      "line2": "Максимальная сумма переводов на 1 кошелек: {maxamount} в месяц или эквивалент в выбранной валюте."
    },
    "restriction_text_1": "В связи с требованиями соблюдения политик WEBMONEY TRANSFER, с 27.03.2020 физическим лицам ограничивается вывод средств на кошельки WebMoney.",
    "restriction_text_2": "All other withdrawal methods (YooMoney, Qiwi, Visa/MasterCard, Mobile operators, Bitcoin, Tether) remain available without changes."
  }
}
</i18n>
